"use client";

import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import Image from "next/image";

import {
  getProductImageOptimizerSrc,
  octogenImageLoader,
  shouldUseProductImageOptimizerLoader,
} from "@/lib/product-image-optimizer";
import { type Product, getProductImage, getProductImageSources } from "@/lib/product-utils";

export interface ProductImageProps {
  // Either provide a src string directly OR a product object
  src?: string;
  fallbackSrc?: string;
  product?: Product | null;
  alt: string;
  objectFit: string;
  onLoad?: (img: { naturalWidth: number; naturalHeight: number }) => void;
  onError?: () => void;
  priority?: boolean;
  loading?: "lazy" | "eager";
  style?: React.CSSProperties;
  className?: string;
}

/**
 * Memoized image component to prevent unnecessary re-renders and image refetching
 * when parent component state changes (e.g., hover states).
 * This component only re-renders when its specific props change.
 */
const PLACEHOLDER_IMAGE = "/placeholder.jpg";

function uniqueFallbacks(primary: string, candidates: Array<string | undefined>): string[] {
  const fallbacks: string[] = [];
  for (const candidate of candidates) {
    if (!candidate || candidate === primary || fallbacks.includes(candidate)) {
      continue;
    }
    fallbacks.push(candidate);
  }
  return fallbacks;
}

function createRenderableSource(primary: string, fallback?: string) {
  const optimizedPrimary = getProductImageOptimizerSrc(primary);
  return {
    primary: optimizedPrimary,
    fallbacks: uniqueFallbacks(optimizedPrimary, [
      optimizedPrimary !== primary ? primary : undefined,
      fallback,
    ]),
  };
}

const ProductImage = memo<ProductImageProps>(
  ({
    src,
    fallbackSrc,
    product,
    alt,
    objectFit,
    onLoad: onLoadCallback,
    onError,
    priority = false,
    loading = "lazy",
    style,
    className,
  }) => {
    const resolvedSources = useMemo(() => {
      if (src) {
        return createRenderableSource(
          src,
          fallbackSrc && fallbackSrc !== src ? fallbackSrc : undefined
        );
      }

      if (product) {
        const [source] = getProductImageSources(product);
        if (source) {
          const fallbackCandidate =
            fallbackSrc && fallbackSrc !== source.primary ? fallbackSrc : source.fallback;

          return createRenderableSource(source.primary, fallbackCandidate);
        }

        const primary = getProductImage(product);
        return createRenderableSource(
          primary,
          fallbackSrc && fallbackSrc !== primary ? fallbackSrc : undefined
        );
      }

      return {
        primary: PLACEHOLDER_IMAGE,
        fallbacks: [],
      };
    }, [src, fallbackSrc, product]);

    const fallbackIndexRef = useRef(0);
    const isMountedRef = useRef(true);
    const [currentSrc, setCurrentSrc] = useState<string>(
      resolvedSources.primary || PLACEHOLDER_IMAGE
    );

    useEffect(() => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    const updateCurrentSrc = useCallback((value: string) => {
      if (!isMountedRef.current) return;
      setCurrentSrc(value);
    }, []);

    useEffect(() => {
      // Only reset to primary if the primary source actually changed
      fallbackIndexRef.current = 0;
      updateCurrentSrc(resolvedSources.primary || PLACEHOLDER_IMAGE);
    }, [resolvedSources.primary, updateCurrentSrc]);

    const effectiveSrc = currentSrc || PLACEHOLDER_IMAGE;
    const imageLoader = shouldUseProductImageOptimizerLoader(effectiveSrc)
      ? octogenImageLoader
      : undefined;

    // Bypass Vercel's image optimizer for every remote product image — the
    // catalog has millions of unique URLs on cdn.octogen.ai, and routing
    // each one through /_next/image generates a transformation + cache
    // write per unique (url, width, format) tuple, which blows through the
    // Image Optimization budget. Local assets (placeholder, etc.) are
    // bounded in count and still get optimized. If the Octogen product image
    // optimizer is enabled, let Next/Image use that custom loader instead.
    const shouldUnoptimize = imageLoader ? false : !effectiveSrc.startsWith("/");

    const baseClassName = `absolute inset-0 w-full h-full object-${objectFit}`;
    const finalClassName = className ? `${baseClassName} ${className}` : baseClassName;

    const handleLoad = onLoadCallback
      ? (event: React.SyntheticEvent<HTMLImageElement>) => {
          const img = event.currentTarget;
          onLoadCallback({
            naturalWidth: img.naturalWidth,
            naturalHeight: img.naturalHeight,
          });
        }
      : undefined;

    const handleError = () => {
      const fallbackIndex = resolvedSources.fallbacks.findIndex(
        (candidate, index) => index >= fallbackIndexRef.current && candidate !== effectiveSrc
      );
      const fallbackCandidate =
        fallbackIndex >= 0 ? resolvedSources.fallbacks[fallbackIndex] : undefined;

      if (fallbackCandidate) {
        fallbackIndexRef.current = fallbackIndex + 1;
        if (process.env.NODE_ENV !== "production") {
          console.warn("ProductImage: falling back to original", {
            primary: resolvedSources.primary,
            fallback: fallbackCandidate,
          });
        }
        updateCurrentSrc(fallbackCandidate);
        return;
      }

      fallbackIndexRef.current = resolvedSources.fallbacks.length;
      updateCurrentSrc(PLACEHOLDER_IMAGE);
      onError?.();
    };

    return (
      <Image
        key={effectiveSrc}
        src={effectiveSrc}
        alt={alt}
        loader={imageLoader}
        width={300}
        height={400}
        // Updated sizes to better match actual card widths and reduce variant generation
        // This ensures Next.js generates predictable URLs that match our prefetch
        sizes="(max-width: 640px) 50vw, (max-width: 1280px) 33vw, (max-width: 1536px) 25vw, 20vw"
        className={finalClassName}
        onLoad={handleLoad}
        style={style}
        onError={handleError}
        quality={70}
        decoding="async"
        fetchPriority={priority ? "high" : undefined}
        unoptimized={shouldUnoptimize}
        priority={priority}
        loading={loading}
        placeholder="empty"
      />
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.src === nextProps.src &&
      prevProps.fallbackSrc === nextProps.fallbackSrc &&
      prevProps.product === nextProps.product &&
      prevProps.alt === nextProps.alt &&
      prevProps.objectFit === nextProps.objectFit &&
      prevProps.priority === nextProps.priority &&
      prevProps.loading === nextProps.loading &&
      prevProps.className === nextProps.className
    );
  }
);

ProductImage.displayName = "ProductImage";

export default ProductImage;
